// src/components/Modal/VideoFrameSelectionModal.tsx - Two-step flow like AIImageModalFresh
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faRandom, faClock, faImage, faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';
import axios from 'axios';

interface ExtractedFrame {
  timestamp: number;
  imageData: string;
  frameNumber: number;
}

interface VideoFrameSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFrameSelected: (imageData: string, timestamp: number) => void;
  videoFile: File | { 
    isProjectVideo: true; 
    projectId: string; 
    projectTitle: string; 
    s3Url: string;
    name: string;
    type: string;
    size: number;
  } | null;
  maxFileSize?: number;
}

const VideoFrameSelectionModal: React.FC<VideoFrameSelectionModalProps> = ({
  isOpen,
  onClose,
  onFrameSelected,
  videoFile,
  maxFileSize = 2 * 1024 * 1024 * 1024
}) => {
  const { t } = useTranslation('thumbnailModal');
  const { showNotification } = useNotification();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Two-step flow states
  const [currentStep, setCurrentStep] = useState<'setup' | 'results'>('setup');
  const [isExtracting, setIsExtracting] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // Add upload loading state
  
  // Video states
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [extractedFrames, setExtractedFrames] = useState<ExtractedFrame[]>([]);
  const [manualTimestamp, setManualTimestamp] = useState(0);
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [videoMetadata, setVideoMetadata] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);

  // Initialize video when file changes
  useEffect(() => {
    if (videoFile && isOpen) {
      console.log('🎬 VideoFrameSelectionModal: Initializing video...', videoFile);
      setCurrentStep('setup');
      setExtractedFrames([]);
      setSelectedFrame(null);
      setIsLoadingVideo(true);
      
      // Handle project video (stream URL)
      if ('isProjectVideo' in videoFile && videoFile.isProjectVideo) {
        console.log('📡 PROJECT VIDEO DETECTED');
        console.log('🔗 S3 URL:', (videoFile as any).s3Url);
        console.log('📋 Project ID:', videoFile.projectId);
        console.log('📝 Project Title:', videoFile.projectTitle);
        
        // Use the S3 URL directly for video playback (works without CORS issues)
        // For frame extraction, we'll create a temporary video element with proper CORS handling
        console.log('🎯 Setting video URL to S3 URL for playback...');
        setVideoUrl((videoFile as any).s3Url);
        return;
      }
      
      // Handle regular file upload
      const file = videoFile as File;
      if (file.size > maxFileSize) {
        showNotification({
          title: 'File Too Large',
          message: `File too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024 * 1024))}GB.`,
          type: 'error'
        });
        onClose();
        return;
      }

      const allowedTypes = [
        'video/mp4', 
        'video/avi', 
        'video/mov',           // Standard .mov MIME type
        'video/quicktime',     // Alternative .mov MIME type (most common)
        'video/x-quicktime',   // Another .mov MIME type variant
        'video/wmv', 
        'video/x-ms-wmv',      // Windows Media Video
        'video/flv', 
        'video/x-flv',         // Flash Video
        'video/webm', 
        'video/mkv',
        'video/x-matroska',    // Matroska Video
        '',                    // Sometimes files don't have MIME type set
        'application/octet-stream' // Generic binary type (fallback)
      ];
      
      const allowedExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      // Check both MIME type and file extension for better compatibility
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        showNotification({
          title: 'Invalid File Type',
          message: 'Please select a valid video file (.mp4, .avi, .mov, .wmv, .flv, .webm, .mkv).',
          type: 'error'
        });
        onClose();
        return;
      }

      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [videoFile, isOpen, maxFileSize, onClose, showNotification]);

  // Direct video URL approach (like SoundGenerator) - Simple and fast!
  useEffect(() => {
    // Use the video URL directly without signed URL complexity
    if (videoUrl) {
      setSignedVideoUrl(videoUrl);
    } else {
      setSignedVideoUrl(null);
    }
  }, [videoUrl]);

  const handleVideoLoaded = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setDuration(video.duration);
      setVideoMetadata({
        width: video.videoWidth,
        height: video.videoHeight
      });
      // Set initial position to start of video (00:00) and sync manual timestamp
      const startTime = 0;
      video.currentTime = startTime;
      setCurrentTime(startTime);
      setManualTimestamp(startTime);
      setIsLoadingVideo(false); // Video is ready
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      // Sync manual timestamp with current playback position
      setManualTimestamp(videoRef.current.currentTime);
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const seekToTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
      // Sync manual timestamp when user seeks
      setManualTimestamp(time);
    }
  };

  const extractFrameAtTime = async (timestamp: number): Promise<string | null> => {
    if (!videoRef.current || !canvasRef.current) return null;

    // For project videos, use proxy to avoid CORS issues
    const isProjectVideo = videoFile && 'isProjectVideo' in videoFile && videoFile.isProjectVideo;
    
    if (isProjectVideo) {
      // Create a temporary video element that loads through proxy
      const tempVideo = document.createElement('video');
      const s3Url = (videoFile as any).s3Url;
      
      // Get auth token
      const token = localStorage.getItem('authToken');
      
      // Load video through proxy with auth
      const proxiedUrl = getApiUrl(`/api/proxy-video?url=${encodeURIComponent(s3Url)}`);
      
      return new Promise((resolve) => {
        tempVideo.crossOrigin = 'anonymous';
        tempVideo.preload = 'metadata';
        
        tempVideo.onloadedmetadata = () => {
          tempVideo.currentTime = timestamp;
        };
        
        tempVideo.onseeked = () => {
          const canvas = canvasRef.current;
          if (!canvas) {
            resolve(null);
            return;
          }
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          
          canvas.width = 1536;
          canvas.height = 864;
          ctx.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);
          
          const dataUrl = canvas.toDataURL('image/png');
          tempVideo.remove();
          resolve(dataUrl);
        };
        
        tempVideo.onerror = () => {
          console.error('❌ Failed to load video for frame extraction');
          tempVideo.remove();
          resolve(null);
        };
        
        // Set src with auth header using fetch + blob URL workaround
        fetch(proxiedUrl, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        })
          .then(response => response.blob())
          .then(blob => {
            tempVideo.src = URL.createObjectURL(blob);
          })
          .catch(error => {
            console.error('❌ Failed to fetch video through proxy:', error);
            tempVideo.remove();
            resolve(null);
          });
      });
    }

    // For regular file uploads, use the current video element directly
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return null;

    canvas.width = 1536;
    canvas.height = 864;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    return canvas.toDataURL('image/png');
  };

  const extractSingleFrame = async () => {
    if (!videoRef.current) return;
    
    setIsExtracting(true);
    
    try {
      // For regular file uploads, seek first
      const isProjectVideo = videoFile && 'isProjectVideo' in videoFile && videoFile.isProjectVideo;
      
      if (!isProjectVideo) {
        videoRef.current.currentTime = manualTimestamp;
        
        await new Promise(resolve => {
          const handleSeeked = () => {
            videoRef.current?.removeEventListener('seeked', handleSeeked);
            resolve(void 0);
          };
          videoRef.current?.addEventListener('seeked', handleSeeked);
        });
      }
      
      const imageData = await extractFrameAtTime(manualTimestamp);
      
      if (imageData) {
        const newFrame: ExtractedFrame = {
          timestamp: manualTimestamp,
          imageData,
          frameNumber: 1
        };
        
        setExtractedFrames([newFrame]);
        setSelectedFrame(1); // Auto-select the single frame
        setCurrentStep('results');
      }
    } catch (error) {
      console.error('❌ Frame extraction failed:', error);
      showNotification({
        title: 'Extraction Failed',
        message: error instanceof Error ? error.message : 'Failed to extract frame. This might be due to video source restrictions.',
        type: 'error'
      });
    }
    
    setIsExtracting(false);
  };

  const extractRandomFrames = async () => {
    if (!videoRef.current || duration === 0) return;
    
    setIsExtracting(true);
    
    try {
      // Divide video into 4 equal sections and extract one random frame from each
      const sectionDuration = duration / 4;
      const timestamps = [];
      
      for (let i = 0; i < 4; i++) {
        const sectionStart = i * sectionDuration;
        const sectionEnd = (i + 1) * sectionDuration;
        const randomTimestamp = sectionStart + (Math.random() * (sectionEnd - sectionStart));
        timestamps.push(randomTimestamp);
      }
      
      timestamps.sort((a, b) => a - b);
      
      const frames: ExtractedFrame[] = [];
      const isProjectVideo = videoFile && 'isProjectVideo' in videoFile && videoFile.isProjectVideo;
      
      for (let i = 0; i < timestamps.length; i++) {
        const timestamp = timestamps[i];
        
        // Only seek for regular file uploads
        if (!isProjectVideo && videoRef.current) {
          videoRef.current.currentTime = timestamp;
          
          await new Promise(resolve => {
            const handleSeeked = () => {
              videoRef.current?.removeEventListener('seeked', handleSeeked);
              resolve(void 0);
            };
            videoRef.current?.addEventListener('seeked', handleSeeked);
          });
        }
        
        const imageData = await extractFrameAtTime(timestamp);
        
        if (imageData) {
          frames.push({
            timestamp,
            imageData,
            frameNumber: i + 1
          });
        }
      }
      
      setExtractedFrames(frames);
      setCurrentStep('results');
    } catch (error) {
      console.error('❌ Random frame extraction failed:', error);
      showNotification({
        title: 'Extraction Failed',
        message: error instanceof Error ? error.message : 'Failed to extract random frames. This might be due to video source restrictions.',
        type: 'error'
      });
    }
    
    setIsExtracting(false);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFrameSelect = (frame: ExtractedFrame) => {
    setSelectedFrame(frame.frameNumber);
    // Don't call onFrameSelected immediately - wait for user to click Done button
  };

  const handleDoneButton = async () => {
    const selectedFrameData = extractedFrames.find(f => f.frameNumber === selectedFrame);
    if (selectedFrameData) {
      setIsUploading(true);
      try {
        await onFrameSelected(selectedFrameData.imageData, selectedFrameData.timestamp);
        onClose();
      } catch (error) {
        console.error('Error uploading frame:', error);
        // Don't close modal on error, let user retry
      } finally {
        setIsUploading(false);
      }
    }
  };

  const goBackToSetup = () => {
    setCurrentStep('setup');
    setExtractedFrames([]);
    setSelectedFrame(null);
    setIsExtracting(false);
    setIsUploading(false); // Reset upload state
  };

  const handleClose = () => {
    setCurrentStep('setup');
    setExtractedFrames([]);
    setSelectedFrame(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setIsUploading(false); // Reset upload state
    if (videoRef.current) {
      videoRef.current.pause();
    }
    onClose();
  };

  if (!isOpen) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '900px',
        maxHeight: '90vh',
        overflow: 'hidden',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {currentStep === 'results' && (
              <button
                onClick={goBackToSetup}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8b5cf6',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span style={{ fontSize: '14px' }}>Back</span>
              </button>
            )}
            
            <h2 style={{
              margin: 0,
              color: '#8b5cf6',
              fontSize: '24px',
              fontWeight: '600'
            }}>
              {currentStep === 'setup' ? 
                (videoFile && 'isProjectVideo' in videoFile && videoFile.isProjectVideo ? 
                  `🎬 ${t('thumbnailModal.frameExtraction.title').replace('"Project Video"', `"${videoFile.projectTitle}"`)}` : 
                  `🎬 ${t('thumbnailModal.frameExtraction.titleGeneric')}`
                ) : 
                `🎯 ${t('thumbnailModal.frameExtraction.titleResults')}`
              }
            </h2>
          </div>
          
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b5cf6',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          maxHeight: 'calc(90vh - 100px)',
          overflowY: 'auto'
        }}>
          {/* Loading Overlay */}
          {(isExtracting || isUploading) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                color: '#8b5cf6',
                fontSize: '18px',
                textAlign: 'center'
              }}>
                <div style={{ marginBottom: '10px' }}>
                  {isExtracting ? t('thumbnailModal.frameExtraction.extractingFrames') : t('thumbnailModal.frameExtraction.uploadingFrames')}
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '3px solid rgba(139, 92, 246, 0.3)',
                  borderTop: '3px solid #8b5cf6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto'
                }} />
                {isUploading && (
                  <div style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    color: '#ccc'
                  }}>
                    Creating thumbnail project...
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* STEP 1: Setup Screen */}
          {currentStep === 'setup' && videoUrl && (
            <>
              {/* Video Loading Overlay */}
              {isLoadingVideo && (
                <div style={{
                  backgroundColor: '#2a2a2a',
                  borderRadius: '8px',
                  padding: '40px 20px',
                  marginBottom: '20px',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    border: '4px solid rgba(139, 92, 246, 0.3)',
                    borderTop: '4px solid #8b5cf6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div style={{
                    color: '#8b5cf6',
                    fontSize: '18px',
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    {videoFile && 'isProjectVideo' in videoFile && videoFile.isProjectVideo ? 
                      <>📡 {t('thumbnailModal.frameExtraction.loadingVideo')}</> : 
                      <>📼 {t('thumbnailModal.frameExtraction.loadingVideoFile')} </>
                    }
                  </div>
                  <div style={{
                    color: '#ccc',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    {t('thumbnailModal.frameExtraction.preparingVideo')}
                  </div>
                </div>
              )}

              {/* Video Player - Simple direct loading like SoundGenerator */}
              <div style={{
                backgroundColor: '#2a2a2a',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                opacity: isLoadingVideo ? 0.5 : 1,
                transition: 'opacity 0.3s ease'
              }}>
                <video
                  ref={videoRef}
                  src={signedVideoUrl || videoUrl}
                  style={{
                    width: '100%',
                    height: '300px',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    backgroundColor: '#000'
                  }}
                  preload="metadata"
                  onLoadedMetadata={handleVideoLoaded}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onLoadStart={() => {
                    console.log('🎬 Video loading started');
                    setIsLoadingVideo(true);
                  }}
                  onCanPlay={() => {
                    console.log('✅ Video ready to play');
                    setIsLoadingVideo(false);
                  }}
                  onError={(e) => {
                    console.error('❌ Video loading error:', e);
                    setIsLoadingVideo(false);
                    showNotification({
                      title: 'Video Error',
                      message: 'Failed to load video. Please try again.',
                      type: 'error'
                    });
                  }}
                />
                
                {/* Video Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginTop: '15px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={togglePlayPause}
                    style={{
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                  
                  <div style={{ color: '#ccc', fontSize: '14px' }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                  
                  {videoMetadata && (
                    <div style={{ color: '#999', fontSize: '12px' }}>
                      {videoMetadata.width}x{videoMetadata.height}
                    </div>
                  )}
                </div>
                
                {/* Time Slider */}
                <input
                  type="range"
                  min={0}
                  max={duration}
                  value={currentTime}
                  onChange={(e) => seekToTime(Number(e.target.value))}
                  style={{
                    width: '100%',
                    marginTop: '15px',
                    height: '6px',
                    borderRadius: '3px',
                    background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${(currentTime / duration) * 100}%, #444 ${(currentTime / duration) * 100}%, #444 100%)`,
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                />
              </div>

              {/* Extraction Options */}
              <div style={{
                backgroundColor: '#2a2a2a',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#8b5cf6', fontSize: '18px' }}>
                  {t('thumbnailModal.frameExtraction.chooseMethod')}
                </h3>
                
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  alignItems: 'start'
                }}>
                  {/* Random Frames Option */}
                  <div style={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      color: '#8b5cf6',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}>
                      <FontAwesomeIcon icon={faRandom} />
                      {t('thumbnailModal.frameExtraction.randomFrames.title')}
                    </div>
                    <p style={{
                      color: '#ccc',
                      fontSize: '14px',
                      margin: '0 0 15px 0',
                      lineHeight: '1.4'
                    }}>
                      {t('thumbnailModal.frameExtraction.randomFrames.description')}
                    </p>
                    <button
                      onClick={extractRandomFrames}
                      disabled={duration === 0 || isLoadingVideo}
                      style={{
                        backgroundColor: (duration === 0 || isLoadingVideo) ? '#444' : '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 20px',
                        cursor: (duration === 0 || isLoadingVideo) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        width: '100%',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isLoadingVideo ? t('thumbnailModal.frameExtraction.loading') : t('thumbnailModal.frameExtraction.randomFrames.button')}
                    </button>
                  </div>

                  {/* Current Position Option */}
                  <div style={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      color: '#10b981',
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}>
                      <FontAwesomeIcon icon={faImage} />
                      {t('thumbnailModal.frameExtraction.currentPosition.title')}
                    </div>
                    <p style={{
                      color: '#ccc',
                      fontSize: '14px',
                      margin: '0 0 8px 0',
                      lineHeight: '1.4'
                    }}>
                      {t('thumbnailModal.frameExtraction.currentPosition.description')}
                    </p>
                    <div style={{
                      color: '#10b981',
                      fontSize: '13px',
                      fontWeight: '500',
                      marginBottom: '15px'
                    }}>
                      📍 {formatTime(currentTime)}
                    </div>
                    <button
                      onClick={() => {
                        setManualTimestamp(currentTime);
                        extractSingleFrame();
                      }}
                      disabled={duration === 0 || isLoadingVideo}
                      style={{
                        backgroundColor: (duration === 0 || isLoadingVideo) ? '#444' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 20px',
                        cursor: (duration === 0 || isLoadingVideo) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        width: '100%',
                        boxShadow: (duration === 0 || isLoadingVideo) ? 'none' : '0 2px 8px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isLoadingVideo ? t('thumbnailModal.frameExtraction.loading') : t('thumbnailModal.frameExtraction.currentPosition.button')}
                    </button>
                  </div>
                </div>

                {/* Advanced Options - Collapsible */}
                <div style={{
                  marginTop: '20px',
                  borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                  paddingTop: '15px'
                }}>
                  <details style={{ color: '#8b5cf6' }}>
                    <summary style={{
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginBottom: '15px',
                      userSelect: 'none'
                    }}>
                      🔧 {t('thumbnailModal.frameExtraction.advanced.title')}
                    </summary>
                    <div style={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '6px',
                      padding: '15px',
                      marginTop: '10px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        justifyContent: 'center'
                      }}>
                        <label style={{ color: '#ccc', fontSize: '14px' }}>
                          {t('thumbnailModal.frameExtraction.advanced.timeLabel')}
                        </label>
                        <input
                          type="number"
                          value={Math.round(manualTimestamp * 10) / 10}
                          onChange={(e) => {
                            const newTime = Number(e.target.value);
                            setManualTimestamp(newTime);
                            seekToTime(newTime);
                          }}
                          min={0}
                          max={duration}
                          step={0.1}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid rgba(139, 92, 246, 0.3)',
                            borderRadius: '4px',
                            backgroundColor: '#2a2a2a',
                            color: 'white',
                            fontSize: '14px',
                            outline: 'none',
                            width: '90px',
                            textAlign: 'center'
                          }}
                        />
                        <button
                          onClick={extractSingleFrame}
                          disabled={duration === 0}
                          style={{
                            backgroundColor: duration === 0 ? '#444' : '#6b46c1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            cursor: duration === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {t('thumbnailModal.frameExtraction.advanced.button')}
                        </button>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            </>
          )}

          {/* STEP 2: Results Screen */}
          {currentStep === 'results' && extractedFrames.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#8b5cf6', fontSize: '18px' }}>
                {extractedFrames.length === 1 ? t('thumbnailModal.frameExtraction.yourExtractedFrame'): t('thumbnailModal.frameExtraction.chooseYourFrameSelect')}
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: extractedFrames.length === 1 ? '1fr' : 'repeat(2, 1fr)', 
                gap: '15px',
                marginBottom: '20px',
                justifyItems: extractedFrames.length === 1 ? 'center' : 'stretch'
              }}>
                {extractedFrames.map((frame) => (
                  <div
                    key={frame.frameNumber}
                    onClick={() => handleFrameSelect(frame)}
                    style={{
                      position: 'relative',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: selectedFrame === frame.frameNumber ? '3px solid #8b5cf6' : '2px solid transparent',
                      transition: 'all 0.2s ease',
                      transform: selectedFrame === frame.frameNumber ? 'scale(1.02)' : 'scale(1)',
                      maxWidth: extractedFrames.length === 1 ? '600px' : 'none',
                      width: '100%'
                    }}
                  >
                    <img
                      src={frame.imageData}
                      alt={`Frame ${frame.frameNumber}`}
                      style={{
                        width: '100%',
                        height: extractedFrames.length === 1 ? '400px' : '200px',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                      color: 'white',
                      padding: extractedFrames.length === 1 ? '15px' : '10px',
                      fontSize: extractedFrames.length === 1 ? '16px' : '14px'
                    }}>
                      <div style={{ fontWeight: '500' }}>
                        {extractedFrames.length === 1 ? t('thumbnailModal.frameExtraction.extractedFrame') : `Frame ${frame.frameNumber}`}
                      </div>
                      <div style={{ 
                        fontSize: extractedFrames.length === 1 ? '14px' : '12px', 
                        opacity: 0.8 
                      }}>
                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px' }} />
                        {formatTime(frame.timestamp)}
                      </div>
                    </div>

                    {selectedFrame === frame.frameNumber && (
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        borderRadius: '50%',
                        width: extractedFrames.length === 1 ? '40px' : '30px',
                        height: extractedFrames.length === 1 ? '40px' : '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: extractedFrames.length === 1 ? '18px' : '14px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Done Button and Back Button - Same style as AIImageModalFresh */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                {/* Back to Extraction Button */}
                <button
                  onClick={goBackToSetup}
                  disabled={isUploading}
                  style={{
                    backgroundColor: 'transparent',
                    color: isUploading ? '#666' : '#8b5cf6',
                    border: `1px solid ${isUploading ? '#666' : 'rgba(139, 92, 246, 0.5)'}`,
                    borderRadius: '8px',
                    padding: '10px 20px',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  {t('thumbnailModal.frameExtraction.extractDifferentFrames')} 
                </button>

                {/* Done Button - only show if frame is selected */}
                {selectedFrame && (
                  <button
                    onClick={handleDoneButton}
                    disabled={isUploading}
                    style={{
                      backgroundColor: isUploading ? '#666' : '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 25px',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s ease',
                      boxShadow: isUploading ? 'none' : '0 2px 8px rgba(139, 92, 246, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isUploading ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        {t('thumbnailModal.frameExtraction.uploading')}  
                      </>
                    ) : (
                      <>
                        {t('thumbnailModal.frameExtraction.useThisFrame')}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Selection Status Text */}
              {selectedFrame && (
                <div style={{
                  textAlign: 'center',
                  marginTop: '15px',
                  color: '#8b5cf6',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {t('thumbnailModal.frameExtraction.frame')} {selectedFrame} {t('thumbnailModal.frameExtraction.selectedAt')} {formatTime(extractedFrames.find(f => f.frameNumber === selectedFrame)?.timestamp || 0)}
                </div>
              )}
            </div>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          {currentStep === 'setup' && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '15px',
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid rgba(139, 92, 246, 0.2)'
            }}>
              <button
                onClick={handleClose}
                style={{
                  backgroundColor: 'transparent',
                  color: '#999',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('thumbnailModal.frameExtraction.cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VideoFrameSelectionModal;
